import { z } from 'zod'
import { productSummarySchema } from './catalog.ts'
import { currencySchema } from './money.ts'

/**
 * The negotiation ladder: cart -> price request -> quote -> counter -> order.
 *
 * The rule that shapes all of it (docs/01-product-design.md §4): numbers live in
 * structured offers, prose lives in chat. A free-text argument cannot be turned
 * into an order, and non-technical users lose track of what was agreed.
 */

// ---------------------------------------------------------------------------
// Cart — server-side, because a contractor starts on site and finishes at the desk
// ---------------------------------------------------------------------------

export const cartLineSchema = z.object({
  id: z.string(),
  productId: z.string(),
  product: productSummarySchema,
  qty: z.number().int(),
  /** Resolved from the live tier ladder at read time, never stored on the line. */
  unitPriceMinor: z.number().int().nullable(),
  lineTotalMinor: z.number().int().nullable(),
  /** How much more to add to reach the next price break, if there is one. */
  nextBreakAddQty: z.number().int().nullable(),
  nextBreakSavingBps: z.number().int().nullable(),
  belowMoq: z.boolean(),
  moq: z.number().int(),
  addedAt: z.number().int(),
})
export type CartLine = z.infer<typeof cartLineSchema>

export const cartSchema = z.object({
  id: z.string(),
  currency: currencySchema,
  lines: z.array(cartLineSchema),
  subtotalMinor: z.number().int(),
  taxMinor: z.number().int(),
  totalMinor: z.number().int(),
  updatedAt: z.number().int(),
})
export type Cart = z.infer<typeof cartSchema>

export const addToCartSchema = z.object({
  productId: z.string().min(1),
  qty: z.number().int().min(1, 'Enter a quantity.'),
})
export type AddToCartInput = z.infer<typeof addToCartSchema>

export const setCartLineSchema = z.object({
  qty: z.number().int().min(0),
})
export type SetCartLineInput = z.infer<typeof setCartLineSchema>

// ---------------------------------------------------------------------------
// Price requests (the database says rfq; the screen never does)
// ---------------------------------------------------------------------------

export const RFQ_STATUSES = [
  'open',
  'quoted',
  'negotiating',
  'accepted',
  'declined',
  'expired',
  'cancelled',
] as const
export type RfqStatus = (typeof RFQ_STATUSES)[number]
export const rfqStatusSchema = z.enum(RFQ_STATUSES)

export const RFQ_STATUS_LABELS: Record<RfqStatus, string> = {
  open: 'Waiting for a price',
  quoted: 'Price sent',
  negotiating: 'Being discussed',
  accepted: 'Agreed',
  declined: 'Turned down',
  expired: 'Ran out of time',
  cancelled: 'Cancelled',
}

/** Which statuses still need someone to act. Drives the admin inbox and the badges. */
export const OPEN_RFQ_STATUSES: readonly RfqStatus[] = ['open', 'quoted', 'negotiating']

export const rfqLineSchema = z.object({
  id: z.string(),
  productId: z.string(),
  product: productSummarySchema,
  qty: z.number().int(),
  note: z.string().nullable(),
})
export type RfqLine = z.infer<typeof rfqLineSchema>

export const rfqSchema = z.object({
  id: z.string(),
  code: z.string(),
  status: rfqStatusSchema,
  lines: z.array(rfqLineSchema),
  note: z.string().nullable(),
  deliveryCity: z.string().nullable(),
  deliveryAddress: z.string().nullable(),
  neededBy: z.number().int().nullable(),
  /**
   * Admin-facing only. The customer is a business or a person with none (D15), so
   * exactly one id is set and `buyerName` is whichever of the two to show.
   */
  buyerOrgId: z.string().nullable(),
  buyerUserId: z.string().nullable(),
  buyerName: z.string().nullable(),
  assignedToUserId: z.string().nullable(),
  assignedToName: z.string().nullable(),
  firstResponseAt: z.number().int().nullable(),
  conversationId: z.string().nullable(),
  offerCount: z.number().int(),
  latestOfferId: z.string().nullable(),
  orderId: z.string().nullable(),
  createdAt: z.number().int(),
  updatedAt: z.number().int(),
  version: z.number().int(),
})
export type Rfq = z.infer<typeof rfqSchema>

export const createRfqSchema = z.object({
  /** Omit to convert the buyer's current cart. */
  lines: z
    .array(
      z.object({
        productId: z.string().min(1),
        qty: z.number().int().min(1),
        note: z.string().trim().max(200).nullish(),
      }),
    )
    .max(50)
    .optional(),
  note: z.string().trim().max(1000).nullish(),
  deliveryCity: z.string().trim().max(80).nullish(),
  deliveryAddress: z.string().trim().max(200).nullish(),
  neededBy: z.number().int().nullish(),
  /**
   * "Buy at the listed price" — the server composes and sends the quote itself so
   * the buyer can accept immediately. Most orders should take this path;
   * negotiation is the escape hatch, not the default (docs/01-product-design.md §4).
   */
  buyAtListed: z.boolean().default(false),
})
export type CreateRfqInput = z.infer<typeof createRfqSchema>

// ---------------------------------------------------------------------------
// Offers — immutable, versioned, expiring
// ---------------------------------------------------------------------------

export const OFFER_STATUSES = [
  'sent',
  'accepted',
  'rejected',
  'superseded',
  'withdrawn',
  'expired',
] as const
export type OfferStatus = (typeof OFFER_STATUSES)[number]
export const offerStatusSchema = z.enum(OFFER_STATUSES)

export const OFFER_STATUS_LABELS: Record<OfferStatus, string> = {
  sent: 'Waiting for an answer',
  accepted: 'Agreed',
  rejected: 'Turned down',
  superseded: 'Replaced by a newer one',
  withdrawn: 'Taken back',
  expired: 'Ran out of time',
}

export const OFFER_DIRECTIONS = ['to_buyer', 'from_buyer'] as const
export type OfferDirection = (typeof OFFER_DIRECTIONS)[number]

export const offerLineSchema = z.object({
  id: z.string(),
  productId: z.string(),
  product: productSummarySchema,
  qty: z.number().int(),
  unitPriceMinor: z.number().int(),
  lineTotalMinor: z.number().int(),
  taxBps: z.number().int(),
  /** Admin-only: what this line costs us, for the live margin readout. */
  costMinor: z.number().int().nullable(),
  note: z.string().nullable(),
})
export type OfferLine = z.infer<typeof offerLineSchema>

export const offerSchema = z.object({
  id: z.string(),
  rfqId: z.string(),
  /** Position in the ladder, starting at 1. The whole ladder stays visible. */
  version: z.number().int(),
  direction: z.enum(OFFER_DIRECTIONS),
  status: offerStatusSchema,
  lines: z.array(offerLineSchema),
  currency: currencySchema,
  subtotalMinor: z.number().int(),
  discountMinor: z.number().int(),
  deliveryFeeMinor: z.number().int(),
  taxMinor: z.number().int(),
  totalMinor: z.number().int(),
  /** Admin-only. */
  costTotalMinor: z.number().int().nullable(),
  marginBps: z.number().int().nullable(),
  note: z.string().nullable(),
  expiresAt: z.number().int().nullable(),
  /** Computed on read — expiry is a state, not a cron job. */
  isExpired: z.boolean(),
  byLabel: z.string(),
  createdAt: z.number().int(),
  respondedAt: z.number().int().nullable(),
})
export type Offer = z.infer<typeof offerSchema>

export const offerLineInputSchema = z.object({
  productId: z.string().min(1),
  qty: z.number().int().min(1),
  /**
   * Omit to let the server price the line from the live ladder. When present it is
   * still only an instruction to the server, which recomputes and snapshots the
   * result — the client is never a source of money (docs/02-architecture.md §8).
   */
  unitPriceMinor: z.number().int().min(0).optional(),
  note: z.string().trim().max(200).nullish(),
})

export const composeOfferSchema = z.object({
  lines: z.array(offerLineInputSchema).min(1, 'A quote needs at least one item.').max(50),
  discountMinor: z.number().int().min(0).default(0),
  deliveryFeeMinor: z.number().int().min(0).default(0),
  note: z.string().trim().max(1000).nullish(),
  /** Hours from now. A quote without a deadline never gets answered. */
  validForHours: z.number().int().min(1).max(720).default(24),
})
export type ComposeOfferInput = z.infer<typeof composeOfferSchema>

/** The buyer's side of the ladder: same shape, minus anything only admin may set. */
export const counterOfferSchema = z.object({
  lines: z
    .array(
      z.object({
        productId: z.string().min(1),
        qty: z.number().int().min(1),
        unitPriceMinor: z.number().int().min(0),
      }),
    )
    .min(1)
    .max(50),
  note: z.string().trim().max(1000).nullish(),
})
export type CounterOfferInput = z.infer<typeof counterOfferSchema>

export const rejectOfferSchema = z.object({
  note: z.string().trim().max(500).nullish(),
})
export type RejectOfferInput = z.infer<typeof rejectOfferSchema>

// ---------------------------------------------------------------------------
// Orders
// ---------------------------------------------------------------------------

export const ORDER_STATUSES = [
  'confirmed',
  'packing',
  'dispatched',
  'delivered',
  'cancelled',
] as const
export type OrderStatus = (typeof ORDER_STATUSES)[number]
export const orderStatusSchema = z.enum(ORDER_STATUSES)

export const ORDER_STATUS_LABELS: Record<OrderStatus, string> = {
  confirmed: 'Confirmed',
  packing: 'Being prepared',
  dispatched: 'On the way',
  delivered: 'Delivered',
  cancelled: 'Cancelled',
}

/**
 * The fulfilment state machine, as data. Every transition the UI offers and every
 * transition the server accepts read from this one table, so they cannot diverge.
 */
export const ORDER_TRANSITIONS: Record<OrderStatus, readonly OrderStatus[]> = {
  confirmed: ['packing', 'dispatched', 'cancelled'],
  packing: ['dispatched', 'cancelled'],
  dispatched: ['delivered'],
  delivered: [],
  cancelled: [],
}

export function canTransitionOrder(from: OrderStatus, to: OrderStatus): boolean {
  return ORDER_TRANSITIONS[from].includes(to)
}

export const orderLineSchema = z.object({
  id: z.string(),
  productId: z.string(),
  /** Snapshotted at accept time, so a later rename does not rewrite history. */
  productName: z.string(),
  brand: z.string().nullable(),
  imageAssetId: z.string().nullable(),
  unitCode: z.string(),
  qty: z.number().int(),
  unitPriceMinor: z.number().int(),
  lineTotalMinor: z.number().int(),
  taxBps: z.number().int(),
})
export type OrderLine = z.infer<typeof orderLineSchema>

export const orderEventSchema = z.object({
  id: z.string(),
  status: orderStatusSchema,
  note: z.string().nullable(),
  byLabel: z.string(),
  createdAt: z.number().int(),
})
export type OrderEvent = z.infer<typeof orderEventSchema>

export const orderSchema = z.object({
  id: z.string(),
  code: z.string(),
  status: orderStatusSchema,
  rfqId: z.string().nullable(),
  offerId: z.string(),
  lines: z.array(orderLineSchema),
  events: z.array(orderEventSchema),
  currency: currencySchema,
  subtotalMinor: z.number().int(),
  discountMinor: z.number().int(),
  deliveryFeeMinor: z.number().int(),
  taxMinor: z.number().int(),
  totalMinor: z.number().int(),
  /** Admin-only. */
  costTotalMinor: z.number().int().nullable(),
  marginBps: z.number().int().nullable(),
  buyerOrgId: z.string().nullable(),
  buyerUserId: z.string().nullable(),
  buyerName: z.string().nullable(),
  deliveryCity: z.string().nullable(),
  deliveryAddress: z.string().nullable(),
  note: z.string().nullable(),
  conversationId: z.string().nullable(),
  placedAt: z.number().int(),
  updatedAt: z.number().int(),
  version: z.number().int(),
})
export type Order = z.infer<typeof orderSchema>

export const advanceOrderSchema = z.object({
  status: orderStatusSchema,
  note: z.string().trim().max(500).nullish(),
})
export type AdvanceOrderInput = z.infer<typeof advanceOrderSchema>

// ---------------------------------------------------------------------------
// Totals — one implementation, used by the composer preview and by the server
// ---------------------------------------------------------------------------

export interface OfferTotalsInput {
  lines: readonly { lineTotalMinor: number; taxBps: number }[]
  discountMinor: number
  deliveryFeeMinor: number
}

export interface OfferTotals {
  subtotalMinor: number
  discountMinor: number
  deliveryFeeMinor: number
  taxMinor: number
  totalMinor: number
}

/**
 * Discount applies to the goods before tax and is spread across lines in
 * proportion to their value, because lines can carry different VAT rates and
 * taxing the undiscounted amount would overcharge.
 */
export function computeOfferTotals(input: OfferTotalsInput): OfferTotals {
  const subtotal = input.lines.reduce((sum, l) => sum + l.lineTotalMinor, 0)
  const discount = Math.min(input.discountMinor, subtotal)

  let tax = 0
  let allocated = 0
  for (const [i, line] of input.lines.entries()) {
    const isLast = i === input.lines.length - 1
    // The last line absorbs the rounding remainder, so the parts always sum to
    // the whole rather than drifting a paisa per line.
    const share =
      subtotal === 0
        ? 0
        : isLast
          ? discount - allocated
          : Math.round((discount * line.lineTotalMinor) / subtotal)
    allocated += share
    tax += Math.round(((line.lineTotalMinor - share) * line.taxBps) / 10_000)
  }

  const total = subtotal - discount + input.deliveryFeeMinor + tax
  return {
    subtotalMinor: subtotal,
    discountMinor: discount,
    deliveryFeeMinor: input.deliveryFeeMinor,
    taxMinor: tax,
    totalMinor: total,
  }
}

/** Human reference like `PR-000123`. Sequence comes from the database. */
export function formatRef(prefix: string, seq: number): string {
  return `${prefix}-${seq.toString().padStart(6, '0')}`
}
